// frontend/src/store/project-general-slice.ts
// Slices own their state, dirty tracking, validation, and payload building
import { useCallback, useEffect, useMemo, useState } from "react";
import { validateProjectData } from "@/utils/validate-project-data";

export type Primitive = string | number | boolean | null | undefined;
export type ProjectData = Record<string, Primitive>;

export type ValidationIssue = {
  path: string;         // e.g., "capacity_kwp"
  message: string;      // e.g., "Must be ≥ 0"
  code?: string;        // optional: "min", "max", "required", "type"
};

export function useProjectGeneralSlice(project: ProjectData | null, projectFieldMetadata?: any[]) {
  const [originalData, setOriginalData] = useState<ProjectData>({});
  const [formData, setFormData] = useState<ProjectData>({});

  // Re-initialize when the active project changes
  useEffect(() => {
    if (project) {
      // setOriginalData(project);
      // setFormData(project);
      // decouple from external references
      const snap = { ...(project as ProjectData) };
      setOriginalData(snap);
      setFormData({ ...snap });
    } else {
      setOriginalData({});
      setFormData({});
    }
  }, [project]);


  // Metadata-driven validation based on changed fields only
  const validate = (): ValidationIssue[] => {
    const fieldsToCheck: Record<string, any> = {};
    for (const k of dirtyKeys) fieldsToCheck[k] = (formData as any)[k];

    const { errors } = validateProjectData(fieldsToCheck, projectFieldMetadata ?? []);
    // Map util's { field, message } to our ValidationIssue shape
    return (errors ?? []).map(e => ({
      path: e.field,
      message: e.message,
    }));
  };


  const updateField = useCallback((name: string, value: Primitive) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetForm = useCallback(() => {
    // ensure a fresh reference for React state and avoid accidental mutations
    setFormData({ ...originalData });
  }, [originalData]);

  const commitForm = useCallback(() => {
    setOriginalData({ ...(formData || {}) });
  }, [formData]);

  const dirtyKeys = useMemo(() => {
    const keys = new Set<string>([
      ...Object.keys(formData || {}),
      ...Object.keys(originalData || {}),
    ]);
    const changed: string[] = [];
    for (const k of keys) {
      if ((formData as any)[k] !== (originalData as any)[k]) changed.push(k);
    }
    return changed;
  }, [formData, originalData]);

  const isDirty = dirtyKeys.length > 0;

  const getChangedFields = (): Record<string, any> => {
    const out: Record<string, any> = {};
    for (const k of dirtyKeys) out[k] = (formData as any)[k];
    return out;
  };

  return {
    formData,
    updateField,
    resetForm,
    commitForm,
    dirtyKeys,
    isDirty,
    validate,
    getChangedFields,
  };
}